import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WebsiteLayout } from './WebsiteLayout';
import { HomePage } from './HomePage';
import { PlatformPage } from './PlatformPage';
import { SolutionsPage } from './SolutionsPage';
import { AboutPage } from './AboutPage';
import { CareersPage } from './CareersPage';
import { ContactPage } from './ContactPage';
import { PricingPage } from './PricingPage';
import { TermsPage } from './TermsPage';
import { PrivacyPage } from './PrivacyPage';
import { VerticalLandingPage } from './VerticalLandingPage';
import { constructionContent } from './verticals/constructionContent';
import { legalContent } from './verticals/legalContent';
import { healthcareContent } from './verticals/healthcareContent';
import { marketingContent } from './verticals/marketingContent';

interface KaryvaWebsiteProps {
  onBack?: () => void;
}

// Per-page <title> + meta description. Keep descriptions ~150 chars and honest
// (no invented stats/claims — see CLAUDE.md / Sprint 33).
const PAGE_SEO: Record<string, { title: string; description: string }> = {
  home: {
    title: 'Karyva — Project & Portfolio Management for Any Industry',
    description: 'Karyva is a customizable project, program, and portfolio management platform built for construction, legal, healthcare, agencies, and more — not just software teams.',
  },
  platform: {
    title: 'Platform — Karyva',
    description: 'Projects, portfolios, tasks, evidence, governance, and reporting in one configurable platform that speaks your industry’s language.',
  },
  solutions: {
    title: 'Solutions — Karyva',
    description: 'See how Karyva adapts to your industry with pre-configured terminology, evidence requirements, and templates.',
  },
  pricing: {
    title: 'Pricing — Karyva',
    description: 'Simple, transparent pricing. Start with a 14-day free trial of the core platform, then scale to AI, dashboards, and integrations when you’re ready.',
  },
  terms: {
    title: 'Terms of Service — Karyva',
    description: 'The terms governing your use of the Karyva project and portfolio management platform.',
  },
  privacy: {
    title: 'Privacy Policy — Karyva',
    description: 'How Karyva collects, uses, stores, and protects your data, including GDPR export and deletion rights available in-product.',
  },
  about: {
    title: 'About Us — Karyva',
    description: 'The team and mission behind Karyva’s industry-agnostic project and portfolio management platform.',
  },
  careers: {
    title: 'Careers — Karyva',
    description: 'Join the team building Karyva’s project and portfolio management platform.',
  },
  contact: {
    title: 'Contact — Karyva',
    description: 'Get in touch with the Karyva team for sales, support, or partnership enquiries.',
  },
};

export function KaryvaWebsite({}: KaryvaWebsiteProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState('home');

  // Sync URL with current page
  useEffect(() => {
    const path = location.pathname.slice(1) || 'home';
    setCurrentPage(path);
  }, [location]);

  // Basic per-page SEO: set <title> + <meta name="description"> on navigation.
  // Client-side only (SPA), but covers shares and keeps tabs/labels sensible.
  useEffect(() => {
    const seo = PAGE_SEO[currentPage] ?? PAGE_SEO.home;
    document.title = seo.title;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'description');
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', seo.description);
  }, [currentPage]);

  const handleNavigate = (page: string) => {
    setCurrentPage(page);
    navigate(page === 'home' ? '/' : `/${page}`);
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <HomePage onNavigate={handleNavigate} />;
      case 'platform':
        return <PlatformPage onNavigate={handleNavigate} />;
      case 'solutions':
        return <SolutionsPage onNavigate={handleNavigate} />;
      case 'about':
        return <AboutPage onNavigate={handleNavigate} />;
      case 'careers':
        return <CareersPage onNavigate={handleNavigate} />;
      case 'pricing':
        return <PricingPage onNavigate={handleNavigate} />;
      case 'contact':
        return <ContactPage onNavigate={handleNavigate} />;
      case 'terms':
        return <TermsPage onNavigate={handleNavigate} />;
      case 'privacy':
        return <PrivacyPage onNavigate={handleNavigate} />;
      // Vertical landing pages (Part 15b — SMB social-proof play).
      case 'for-construction':
        return <VerticalLandingPage content={constructionContent} onNavigate={handleNavigate} />;
      case 'for-legal':
        return <VerticalLandingPage content={legalContent} onNavigate={handleNavigate} />;
      case 'for-healthcare':
        return <VerticalLandingPage content={healthcareContent} onNavigate={handleNavigate} />;
      case 'for-marketing':
        return <VerticalLandingPage content={marketingContent} onNavigate={handleNavigate} />;
      default:
        return <HomePage onNavigate={handleNavigate} />;
    }
  };

  return (
    <WebsiteLayout currentPage={currentPage} onNavigate={handleNavigate}>
      {renderPage()}
    </WebsiteLayout>
  );
}
